import type { ReactNode } from 'react'

import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
import { PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'

/*
 * Loading shapes for a Signal Zone table page, off the loaded boxes
 * (DataTable's panel, Pagination, the segmented control and the footnote) so
 * the pending page and the loaded one are the same height block for block.
 *
 * Its own light module (the skeleton primitives and `cn` only): a route's
 * `pendingComponent` ships in the entry chunk, so it may not pull a component
 * module in (docs/route-loading.md, "Pitfalls"). Bars only, no words.
 */

/**
 * `SignalsToolbar` in bars: tabs or a search box at the left, the date control
 * at the right, as the loaded row lays them out. No margin of its own: the
 * frame and board skeletons that hold it give it the 24px.
 */
export function ToolbarSkeleton({ start, end }: { start?: ReactNode; end?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 grow">{start}</div>
      <div className="flex min-w-0 flex-wrap items-center gap-3">{end}</div>
    </div>
  )
}

/** `TabBar`'s row: 44px below `lg`, 36 from it. */
export function TabsSkeleton({ className }: { className?: string }) {
  return <Skeleton className={cn('h-11 w-64 lg:h-9', className)} />
}

/** The toolbar's search box: full width on a phone, the frame's ~280 from `@sm`, 44px then 36. */
export function SearchSkeleton() {
  return <Skeleton className="h-11 w-full rounded-sm @sm:w-70 lg:h-9" />
}

/**
 * `SignalsTableFrame` before its rows land: the row above the table when the
 * page has one (`toolbar`, already in bars — a `ToolbarSkeleton`, or the
 * Signal page's facts), its title row when it has a `title`, the two-line-row
 * `DataTableSkeleton` panel with no card around it (the frame steps back
 * around a panel), and the pager. `rows` is the opening page (15) or what the
 * page's data typically holds.
 */
export function TableFrameSkeleton({
  rows,
  columns,
  toolbar,
  title = false,
  pager = true,
  tableClassName,
}: {
  rows: number
  /** Columns, the Company column included. */
  columns: number
  toolbar?: ReactNode
  title?: boolean
  pager?: boolean
  /** The panel's own classes: the page's row-height token (`SIGNAL_ROWS_52`). */
  tableClassName?: string
}) {
  return (
    <div aria-hidden="true">
      {toolbar === undefined ? null : <div className="mb-6">{toolbar}</div>}
      {title ? (
        <div className="mb-3 flex min-h-9 items-center justify-between gap-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-16" />
        </div>
      ) : null}
      <DataTableSkeleton rows={rows} columns={columns} rowHeight="lg" className={tableClassName} />
      {pager ? <PagerSkeleton /> : null}
    </div>
  )
}

/** `Pagination`: its own `mt-6`, then a row of rows-per-page and the numbered pager. */
export function PagerSkeleton() {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 lg:gap-y-4">
      <Skeleton className="h-11 w-40 rounded-sm lg:h-9" />
      <Skeleton className="h-11 w-56 rounded-sm lg:h-9" />
    </div>
  )
}

/**
 * A 12/400 note under a table, as wide as its text. `className` is the box's height at each
 * width, in 16px lines (`h-12 @xl:h-4`: three lines in a phone's column, one from `@xl`), since
 * how many lines the real note wraps to is a function of the column and not of the data.
 */
export function FootnoteSkeleton({ className }: { className: string }) {
  return (
    <div aria-hidden="true" className={cn('flex flex-col justify-start gap-1', className)}>
      <Skeleton className="h-3 w-full max-w-lg" />
    </div>
  )
}

/**
 * `SignalTileBoard` before its rows land (Live, Daily): the toolbar (the tabs'
 * 36px row, the page's picker at its right, `toolbarEnd`) 24 over `tiles`
 * 56px tiles in the loaded grid's columns. Bars only, no words.
 */
export function TileBoardSkeleton({
  tiles = 12,
  toolbarEnd = <PickerSkeleton />,
}: {
  tiles?: number
  toolbarEnd?: ReactNode
}) {
  return (
    <div aria-hidden="true" className="@container">
      <div className="mb-6">
        <ToolbarSkeleton start={<TabsSkeleton />} end={toolbarEnd} />
      </div>
      <div className="grid grid-cols-2 gap-4 @xl:grid-cols-3 @3xl:grid-cols-4">
        {Array.from({ length: tiles }, (_, tile) => (
          <Skeleton key={tile} className="h-14" />
        ))}
      </div>
    </div>
  )
}
